import emailjs from "@emailjs/browser";
import { useState } from "react";

/**
 * EmailJS credentials — create a free account at emailjs.com, then:
 *   1. Add an Email Service (Gmail, Outlook, etc.) → copy the Service ID
 *   2. Create an Email Template → copy the Template ID
 *      Template variables used: {{from_name}}, {{from_email}}, {{phone}}, {{message}}
 *   3. Account → API Keys → copy the Public Key
 *
 * Replace the three placeholder strings below with your real values.
 * To keep them out of source control, move them into a .env file:
 *   REACT_APP_EMAILJS_SERVICE_ID=...
 *   REACT_APP_EMAILJS_TEMPLATE_ID=...
 *   REACT_APP_EMAILJS_PUBLIC_KEY=...
 */
const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || "YOUR_SERVICE_ID";
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || "YOUR_PUBLIC_KEY";

/**
 * useSendEmail — reusable hook for sending email via EmailJS.
 *
 * @returns {{ send, sending, sent, error, reset }}
 *
 * `send(templateParams)` — call with an object whose keys match your
 *   EmailJS template variables, e.g.:
 *   { from_name, from_email, phone, message }
 *
 * The template should have "To Email" set to contact@cccsalvation.com
 * inside the EmailJS dashboard (no code change needed to update the recipient).
 */
export const useSendEmail = () => {
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState(null);

  const send = async (templateParams) => {
    setSending(true);
    setError(null);
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      setSent(true);
      return true;
    } catch (err) {
      const msg = err?.text || err?.message || "Failed to send. Please try again.";
      setError(msg);
      return false;
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSent(false);
    setError(null);
  };

  return { send, sending, sent, error, reset };
};
